import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import { FeatureProvider } from './context/FeatureContext';
import { ProtectedRoute, Layout, PageLoading, CommandPalette, ShortcutHelp, ErrorBoundary } from './components';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Core pages - loaded immediately
import { Login, Register, EventList, EventDetail } from './pages';

// Retry wrapper for lazy imports — retries once then reloads on chunk load failure
function lazyRetry(importFn) {
  return lazy(() =>
    importFn().catch(() =>
      // One retry before giving up (handles transient network errors)
      importFn().catch(() => {
        // Chunk is truly missing — force reload to get fresh index.html
        window.location.reload();
        // Return a never-resolving promise so React doesn't render stale content
        return new Promise(() => {});
      })
    )
  );
}

// Lazy-loaded pages - loaded on demand with retry
const ForgotPassword = lazyRetry(() => import('./pages/ForgotPassword'));
const ResetPassword = lazyRetry(() => import('./pages/ResetPassword'));
const VerifyEmail = lazyRetry(() => import('./pages/VerifyEmail'));
const AcceptInvite = lazyRetry(() => import('./pages/AcceptInvite'));
const DashboardRouter = lazyRetry(() => import('./pages/DashboardRouter'));
const AttendeeDashboard = lazyRetry(() => import('./pages/AttendeeDashboard'));
const CreateEvent = lazyRetry(() => import('./pages/CreateEvent'));
const MyEvents = lazyRetry(() => import('./pages/MyEvents'));
const MySchedule = lazyRetry(() => import('./pages/MySchedule'));
const SessionDetail = lazyRetry(() => import('./pages/SessionDetail'));
const OrganizerDashboard = lazyRetry(() => import('./pages/OrganizerDashboard'));
const ManageEvent = lazyRetry(() => import('./pages/ManageEvent'));
const EventAttendees = lazyRetry(() => import('./pages/EventAttendees'));
const AdminDashboard = lazyRetry(() => import('./pages/AdminDashboard'));
const AdminUsers = lazyRetry(() => import('./pages/AdminUsers'));
const AdminEvents = lazyRetry(() => import('./pages/AdminEvents'));
const AdminReports = lazyRetry(() => import('./pages/AdminReports'));
const AdminAuditLogs = lazyRetry(() => import('./pages/AdminAuditLogs'));
const AdminSettings = lazyRetry(() => import('./pages/AdminSettings'));
const AdminHealth = lazyRetry(() => import('./pages/AdminHealth'));
const UserProfile = lazyRetry(() => import('./pages/UserProfile'));
const EventAnalytics = lazyRetry(() => import('./pages/EventAnalytics'));
const ManageAttendees = lazyRetry(() => import('./pages/ManageAttendees'));
const SessionBuilder = lazyRetry(() => import('./pages/SessionBuilder'));
const PreRegistration = lazyRetry(() => import('./pages/PreRegistration'));
const EventBrochure = lazyRetry(() => import('./pages/EventBrochure'));
const SharedSchedule = lazyRetry(() => import('./pages/SharedSchedule'));
const PresenterDirectory = lazyRetry(() => import('./pages/PresenterDirectory'));
const GoogleSheetsImport = lazyRetry(() => import('./pages/GoogleSheetsImport'));
const GoogleCallback = lazyRetry(() => import('./pages/GoogleCallback'));
const PrivacyPolicy = lazyRetry(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazyRetry(() => import('./pages/TermsOfService'));
const RegistrationWizard = lazyRetry(() => import('./pages/RegistrationWizard'));
const PresenterDashboard = lazyRetry(() => import('./pages/PresenterDashboard'));
const PresenterEventView = lazyRetry(() => import('./pages/PresenterEventView'));
const AdminHelp = lazyRetry(() => import('./pages/AdminHelp'));
const CheckInLanding = lazyRetry(() => import('./pages/CheckInLanding'));
const EventCheckIn = lazyRetry(() => import('./pages/EventCheckIn'));
const RegistrationMonitor = lazyRetry(() => import('./pages/RegistrationMonitor'));
const PresenterForm = lazyRetry(() => import('./pages/PresenterForm'));

function AppContent() {
  const {
    isCommandPaletteOpen,
    setIsCommandPaletteOpen,
    isShortcutHelpOpen,
    setIsShortcutHelpOpen,
  } = useKeyboardShortcuts();

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<PageLoading />}>
        <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/accept-invite/:token" element={<AcceptInvite />} />
        <Route path="/presenter-form/:token" element={<PresenterForm />} />
        <Route path="/auth/google/callback" element={<GoogleCallback />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />

        {/* Check-in landing (QR code scanned outside the app) */}
        <Route path="/checkin/:eventId/:token" element={<CheckInLanding />} />

        {/* Event routes */}
        <Route path="/events" element={<EventList />} />
        <Route
          path="/events/create"
          element={
            <ProtectedRoute requiredRoles={['organizer', 'admin']}>
              <CreateEvent />
            </ProtectedRoute>
          }
        />
        <Route path="/events/:slug" element={<EventBrochure />} />
        <Route path="/events/:slug/detail" element={<EventDetail />} />
        <Route path="/events/:slug/presenters" element={<PresenterDirectory />} />
        <Route path="/events/:slug/preregister" element={<PreRegistration />} />
        <Route
          path="/events/:eventId/register"
          element={
            <ProtectedRoute>
              <RegistrationWizard />
            </ProtectedRoute>
          }
        />
        <Route path="/schedule/shared/:token" element={<SharedSchedule />} />

        {/* Session routes */}
        <Route path="/sessions/:id" element={<SessionDetail />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardRouter />
            </ProtectedRoute>
          }
        />

        <Route
          path="/presenter/events/:eventId"
          element={
            <ProtectedRoute>
              <PresenterEventView />
            </ProtectedRoute>
          }
        />

        <Route
          path="/my-events"
          element={
            <ProtectedRoute>
              <MyEvents />
            </ProtectedRoute>
          }
        />

        <Route
          path="/events/:slug/schedule"
          element={
            <ProtectedRoute>
              <MySchedule />
            </ProtectedRoute>
          }
        />

        {/* Organizer routes */}
        <Route
          path="/organizer"
          element={
            <ProtectedRoute requiredRoles={['organizer', 'admin']}>
              <OrganizerDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/events/:id/manage"
          element={
            <ProtectedRoute requiredRoles={['organizer', 'admin']}>
              <ManageEvent />
            </ProtectedRoute>
          }
        />

        <Route
          path="/events/:id/checkin"
          element={
            <ProtectedRoute requiredRoles={['organizer', 'admin']}>
              <EventCheckIn />
            </ProtectedRoute>
          }
        />

        <Route
          path="/events/:id/monitor"
          element={
            <ProtectedRoute requiredRoles={['organizer', 'admin']}>
              <RegistrationMonitor />
            </ProtectedRoute>
          }
        />

        <Route
          path="/events/:id/builder"
          element={
            <ProtectedRoute requiredRoles={['organizer', 'admin']}>
              <SessionBuilder />
            </ProtectedRoute>
          }
        />

        <Route
          path="/events/:id/import-sheets"
          element={
            <ProtectedRoute requiredRoles={['organizer', 'admin']}>
              <GoogleSheetsImport />
            </ProtectedRoute>
          }
        />

        <Route
          path="/events/:id/attendees"
          element={
            <ProtectedRoute>
              <EventAttendees />
            </ProtectedRoute>
          }
        />

        <Route
          path="/organizer/events/:id/analytics"
          element={
            <ProtectedRoute requiredRoles={['organizer', 'admin']}>
              <EventAnalytics />
            </ProtectedRoute>
          }
        />

        <Route
          path="/organizer/events/:id/attendees"
          element={
            <ProtectedRoute requiredRoles={['organizer', 'admin']}>
              <ManageAttendees />
            </ProtectedRoute>
          }
        />

        {/* User settings */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <UserProfile />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/notifications"
          element={
            <ProtectedRoute>
              <UserProfile />
            </ProtectedRoute>
          }
        />

        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <AdminUsers />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/events"
          element={
            <ProtectedRoute>
              <AdminEvents />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/reports"
          element={
            <ProtectedRoute>
              <AdminReports />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/audit-logs"
          element={
            <ProtectedRoute>
              <AdminAuditLogs />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute>
              <AdminSettings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/health"
          element={
            <ProtectedRoute>
              <AdminHealth />
            </ProtectedRoute>
          }
        />

        {/* Help page for organizers/admins */}
        <Route
          path="/help"
          element={
            <ProtectedRoute requiredRoles={['organizer', 'admin']}>
              <AdminHelp />
            </ProtectedRoute>
          }
        />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/events" replace />} />

            {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>

      {/* Global Modals */}
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
      />
      <ShortcutHelp
        isOpen={isShortcutHelpOpen}
        onClose={() => setIsShortcutHelpOpen(false)}
      />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <FeatureProvider>
          <AuthProvider>
            <ToastProvider>
              <Layout>
                <AppContent />
              </Layout>
            </ToastProvider>
          </AuthProvider>
        </FeatureProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function NotFound() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'DM Sans', sans-serif",
      background: '#fafaf9',
    }}>
      <h1 style={{
        fontSize: '6rem',
        fontWeight: '300',
        color: '#e2e8f0',
        margin: '0',
      }}>404</h1>
      <p style={{
        color: '#64748b',
        marginTop: '1rem',
        marginBottom: '2rem',
      }}>Page not found</p>
      <a
        href="/events"
        style={{
          color: '#1a1a2e',
          textDecoration: 'none',
          fontWeight: '500',
        }}
      >
        Browse Events
      </a>
    </div>
  );
}

export default App;
